<template>
    <span v-if="props.billing === ''" data-test="billing" class="text-slate-500">-</span>
    <vx-popover v-else hover :pinnable="props.billing === 'org_unit'" teleport arrow placement="top" class="min-w-0">
        <button
            type="button"
            class="rounded-full cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
            data-test="billing"
        >
            <vx-badge v-if="props.billing === 'incomplete'" color="amber" size="xs">
                {{ billingLabels[props.billing] }}
            </vx-badge>
            <vx-badge v-else-if="props.billing === 'complete'" color="sky" size="xs">
                {{ billingLabels[props.billing] }}
                <font-awesome-icon
                    widthAuto
                    :data-test="`billing-${props.paymentMethod}-icon`"
                    :icon="props.paymentMethod === 'invoice' ? faFileInvoiceDollar : faCreditCard"
                />
            </vx-badge>
            <vx-badge v-else color="slate" size="xs">
                {{ billingLabels[props.billing] }}
                <font-awesome-icon
                    widthAuto
                    :data-test="`billing-${props.billing}-icon`"
                    :icon="props.billing === 'org_unit' ? faBuildingFlag : faCrown"
                    :class="props.billing === 'org_unit' ? 'text-violet-500!' : 'text-amber-400!'"
                />
            </vx-badge>
        </button>
        <template v-slot:content>
            <div class="flex max-w-[260px] flex-col gap-2">
                <div v-if="props.billing === 'org_unit'" data-test="billing-explanation">
                    The group is not billable, so its usage rolls up to its Organizational Unit,
                    <a
                        data-test="billing-org-unit-link"
                        :href="props.orgUnitHref"
                        class="font-bold text-sky-700 no-underline hover:underline"
                    >
                        {{ props.orgUnitName }}</a
                    >.
                </div>
                <div v-else data-test="billing-explanation">{{ billingExplanation }}</div>
                <template v-if="props.billing === 'complete' || props.billing === 'incomplete'">
                    <dl class="m-0 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 text-xs">
                        <template v-for="item in billingItems" :key="item.key">
                            <dt class="text-slate-500">{{ item.label }}</dt>
                            <dd
                                :data-test="`billing-${item.key}`"
                                class="m-0 flex items-center gap-1.5 font-semibold"
                                :class="item.done ? 'text-emerald-700' : 'text-rose-600'"
                            >
                                <font-awesome-icon widthAuto :icon="item.done ? faCheck : faXmark" />
                                {{ item.status }}
                            </dd>
                        </template>
                    </dl>
                    <div
                        v-if="paymentMethodExplanation"
                        data-test="billing-payment-method-explanation"
                        class="text-xs text-slate-500"
                    >
                        {{ paymentMethodExplanation }}
                    </div>
                </template>
            </div>
        </template>
    </vx-popover>
</template>

<script lang="ts" setup>
import {
    faBuildingFlag,
    faCheck,
    faCreditCard,
    faCrown,
    faFileInvoiceDollar,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxPopover } from '@voxie/frontend-components';
import { computed } from 'vue';
import type { KioskTeamGroupBilling, KioskTeamGroupPaymentMethod } from '~/services/adminTeamGroupsClient.types';
import { billingLabels, paymentMethodLabel } from './kioskTeamGroupsTableHelpers';

const props = defineProps<{
    billing: KioskTeamGroupBilling;
    hasRates: boolean;
    hasBillingAddress: boolean;
    paymentMethod: KioskTeamGroupPaymentMethod;
    paymentTermsDays: number;
    orgUnitName: string;
    orgUnitHref: string;
}>();

const billingExplanation = computed(() => {
    if (props.billing === 'complete') {
        return 'Ready to be billed.';
    }

    if (props.billing === 'incomplete') {
        return 'Not ready to be billed yet.';
    }

    return 'The group is not billable, so its usage rolls up to corporate.';
});

const itemStatus = (done: boolean, doneStatus: string, missingStatus: string) => (done ? doneStatus : missingStatus);

const billingItems = computed(() => [
    {
        key: 'rates',
        label: 'Rates',
        done: props.hasRates,
        status: itemStatus(props.hasRates, 'Updated', 'Not updated'),
    },
    {
        key: 'address',
        label: 'Billing address',
        done: props.hasBillingAddress,
        status: itemStatus(props.hasBillingAddress, 'Updated', 'Not updated'),
    },
    {
        key: 'payment-method',
        label: 'Payment method',
        done: props.paymentMethod !== '',
        status: itemStatus(
            props.paymentMethod !== '',
            paymentMethodLabel(props.paymentMethod, props.paymentTermsDays),
            'Not entered'
        ),
    },
]);

const paymentMethodExplanation = computed(() => {
    if (props.paymentMethod === 'card') {
        return 'Paid by card: Stripe charges the card on file automatically.';
    }

    if (props.paymentMethod === 'invoice') {
        return `Paid by invoice: Stripe emails each invoice, due in ${props.paymentTermsDays} days, and the customer pays it from there.`;
    }

    return '';
});
</script>
